import { createSlice } from '@reduxjs/toolkit';
import { advancedFiltersReducers } from './advancedFiltersReducers';

export const FLOW_ACTIVITY_THRESHOLD = 7;
export const NOTICE_ACTIVITY_THRESHOLD = 7;

/**
 * @typedef {'none' | 'shift'} EditorMode
 */

/**
 * @typedef {{
 *  points: {
 *    mapped: boolean | null;
 *    hasCapacityComponent: boolean | null;
 *    hasFlowActivity: boolean | null;
 *    hasNoticeComponent: boolean | null;
 *    hasNoticeActivity: boolean | null;
 *    hasInterconnects: boolean | null;
 *    textSearch: string | null;
 *    tsps: number[];
 *    loc_types: string[];
 *  };
 *  capacityComponents: {
 *    hasPoints: boolean | null;
 *    hasFlowActivity: boolean | null;
 *    hasNoticeComponent: boolean | null;
 *    hasNoticeActivity: boolean | null;
 *    capacityTypes: string[];
 *    directions: string[];
 *    tsps: number[];
 *    textSearch: string | null;
 *  };
 *  noticeComponents: {
 *    hasPoints: boolean | null;
 *    hasCapacityComponent: boolean | null;
 *    hasNoticeActivity: boolean | null;
 *    mapped: boolean | null;
 *    tsps: number[];
 *    textSearch: string | null;
 *    capacityTypes: string[];
 *    directions: string[];
 *  };
 *  priceComponents: {
 *    hasPoints: boolean | null;
 *    mapped: boolean | null;
 *    priceTypes: import('@/types/api/PriceComponent').BuyOrSell[];
 *    tsps: number[];
 *    textSearch: string | null;
 *  };
 *  pathComponents: {
 *    textSearch: string | null;
 *    tsps: number[];
 *    types: import('@/types').PathComponent['type'][];
 *  };
 * }} AdvancedFiltersState
 */

/**
 * @typedef {keyof AdvancedFiltersState} AdvancedFiltersType
 */

/** @type {AdvancedFiltersState} */
const initialState = {
  points: {
    mapped: null,
    hasCapacityComponent: null,
    hasFlowActivity: null,
    hasNoticeComponent: null,
    hasNoticeActivity: null,
    hasInterconnects: null,
    textSearch: '',
    tsps: [],
    loc_types: [],
  },
  capacityComponents: {
    hasPoints: null,
    hasFlowActivity: null,
    hasNoticeComponent: null,
    hasNoticeActivity: null,
    capacityTypes: [],
    directions: [],
    tsps: [],
    textSearch: '',
  },
  noticeComponents: {
    hasPoints: null,
    hasCapacityComponent: null,
    hasNoticeActivity: null,
    tsps: [],
    textSearch: '',
    capacityTypes: [],
    directions: [],
  },
  priceComponents: {
    hasPoints: null,
    mapped: null,
    tsps: [],
    textSearch: '',
    priceTypes: [],
  },
  pathComponents: {
    textSearch: '',
    tsps: [],
    types: [],
  },
};

/** @type {import('@reduxjs/toolkit').Slice<AdvancedFiltersState, typeof advancedFiltersReducers, 'advancedFilters'>} */
export const advancedFiltersSlice = createSlice({
  name: 'advancedFilters',
  initialState,
  reducers: advancedFiltersReducers,
});

export const advancedFiltersActions = advancedFiltersSlice.actions;

export default advancedFiltersSlice.reducer;
